import { useCallback, useEffect, useMemo, useState } from "react";
import { JSONSchema7 } from "json-schema";
import {
  CreateStandardAgentTriggerBody,
  StandardAgentTrigger,
  UpdateStandardAgentTriggerBody,
} from "@forgepoint/shared";
import { Loader2 } from "lucide-react";
import { fetchApi } from "@/lib/api";
import { JSONSchemaForm } from "@/components/JSONSchemaForm";
import {
  JSONSchemaFieldTemplateProps,
  JSONObject,
  JSONValue,
  isJSONObject,
} from "@/components/jsonSchemaForm/types";
import { DynamicField } from "@/modules/agents/components/DynamicField";
import { TriggerDefinition } from "@/store/agentWorkflowTriggersStore";
import { ManualConfiguration } from "@/store/agentsStore";
import { ConnectionSelector } from "@/components/ConnectionSelector";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type TriggerFormContext = {
  configMap: Map<string, ManualConfiguration>;
  triggerId: string;
  triggerConfiguration: ManualConfiguration[];
  connectionId?: string;
  onConfigChange: (
    key: string,
    config: ManualConfiguration | null,
    shouldReloadSchema: boolean,
  ) => void;
  onRefreshSchema: (config: ManualConfiguration[]) => void;
};

interface StandardAgentTriggerEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerDefinition: TriggerDefinition | null;
  initialTrigger: StandardAgentTrigger | null;
  isSaving: boolean;
  onSave: (
    payload: CreateStandardAgentTriggerBody | UpdateStandardAgentTriggerBody,
  ) => Promise<void>;
}

const setNestedValue = (
  obj: JSONObject,
  path: string[],
  value: JSONValue,
): JSONObject => {
  if (path.length === 0) {
    return obj;
  }

  if (path.length === 1) {
    return { ...obj, [path[0]]: value };
  }

  const [firstKey, ...restPath] = path;
  const existingValue = obj[firstKey];
  const currentNested = isJSONObject(existingValue) ? existingValue : {};

  return {
    ...obj,
    [firstKey]: setNestedValue(currentNested, restPath, value),
  };
};

const isJSONValue = (value: unknown): value is JSONValue => {
  if (
    value === undefined ||
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every((item) => isJSONValue(item));
  }

  if (typeof value === "object") {
    return Object.values(value).every((item) => isJSONValue(item));
  }

  return false;
};

const getJSONValue = (value: unknown): JSONValue | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (!isJSONValue(value)) {
    return undefined;
  }

  return value;
};

const buildManualConfigFromFormData = (
  data: JSONObject,
  path: string[],
  configs: ManualConfiguration[],
): void => {
  Object.entries(data).forEach(([key, value]) => {
    const schemaPath = [...path, key];

    if (value === null || Array.isArray(value)) {
      configs.push({
        type: "MANUAL",
        input: value,
        schemaPath,
      });
      return;
    }

    if (typeof value === "object") {
      buildManualConfigFromFormData(value, schemaPath, configs);
      return;
    }

    configs.push({
      type: "MANUAL",
      input: value,
      schemaPath,
    });
  });
};

function TriggerFieldTemplate({
  id,
  label,
  description,
  required,
  error,
  children,
  className,
  schemaPath,
  formContext,
  schema,
  currValue,
}: JSONSchemaFieldTemplateProps<TriggerFormContext>) {
  const isDynamicField = schema.isDynamic;

  if (!formContext) {
    return null;
  }

  if (isDynamicField) {
    const fieldKey = schemaPath.join(".");
    return (
      <div className={className}>
        {label && (
          <Label htmlFor={id}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        {description && (
          <p className="text-sm text-muted-foreground mb-2">{description}</p>
        )}
        <DynamicField
          triggerId={formContext.triggerId}
          fieldName={schemaPath[0]}
          value={currValue}
          existingFieldConfiguration={formContext.triggerConfiguration}
          connectionId={formContext.connectionId}
          shouldRefresh={schema.shouldReload ?? false}
          onRefresh={() =>
            formContext.onRefreshSchema(formContext.triggerConfiguration)
          }
          onChange={(option) => {
            formContext.onConfigChange(
              fieldKey,
              {
                type: "MANUAL",
                input: option.input,
                label: option.label,
                schemaPath,
              },
              schema.shouldReload ?? false,
            );
          }}
        />
        {error && <p className="text-sm text-destructive mt-2">{error}</p>}
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      {description && (
        <p className="text-sm text-muted-foreground mb-2">{description}</p>
      )}
      {children}
      {error && <p className="text-sm text-destructive mt-2">{error}</p>}
    </div>
  );
}

export function StandardAgentTriggerEditorDialog({
  open,
  onOpenChange,
  triggerDefinition,
  initialTrigger,
  isSaving,
  onSave,
}: StandardAgentTriggerEditorDialogProps) {
  const [triggerConfiguration, setTriggerConfiguration] = useState<
    ManualConfiguration[]
  >([]);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [userMessage, setUserMessage] = useState("");
  const [dynamicSchema, setDynamicSchema] = useState<JSONSchema7 | null>(null);
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !triggerDefinition) {
      return;
    }

    if (initialTrigger) {
      setTriggerConfiguration(
        initialTrigger.triggerMetadata.triggerConfiguration.map((config) => ({
          type: "MANUAL",
          schemaPath: config.schemaPath,
          input: getJSONValue(config.input),
          label: config.label,
        })),
      );
      setConnectionId(
        initialTrigger.triggerMetadata.connectionConfig?.connectionId ?? null,
      );
      setUserMessage(initialTrigger.userMessage ?? "");
      return;
    }

    setTriggerConfiguration([]);
    setConnectionId(null);
    setUserMessage("");
  }, [open, initialTrigger, triggerDefinition]);

  const fetchSchema = useCallback(
    async (configuration: ManualConfiguration[]) => {
      if (!triggerDefinition) {
        return;
      }

      setIsLoadingSchema(true);
      setSchemaError(null);
      try {
        const response = await fetchApi("apps/input-schema", "POST", {
          body: {
            triggerId: triggerDefinition.id,
            existingFieldConfiguration: configuration,
            connectionId: connectionId ?? undefined,
          },
        });

        if (!response.ok || !response.data) {
          throw new Error("Failed to fetch trigger schema");
        }

        if (!isSchemaResponse(response.data)) {
          throw new Error("Invalid trigger schema response");
        }

        setDynamicSchema(response.data.schema);
      } catch (error) {
        setSchemaError(
          error instanceof Error
            ? error.message
            : "Failed to fetch trigger schema",
        );
      } finally {
        setIsLoadingSchema(false);
      }
    },
    [triggerDefinition, connectionId],
  );

  useEffect(() => {
    if (!open || !triggerDefinition) {
      return;
    }
    fetchSchema(triggerConfiguration);
  }, [open, triggerDefinition, connectionId]);

  const configMap = useMemo(() => {
    const map = new Map<string, ManualConfiguration>();
    triggerConfiguration.forEach((config) => {
      map.set(config.schemaPath.join("."), config);
    });
    return map;
  }, [triggerConfiguration]);

  const formData = useMemo(() => {
    let data: JSONObject = {};
    triggerConfiguration.forEach((config) => {
      data = setNestedValue(data, config.schemaPath, config.input);
    });
    return data;
  }, [triggerConfiguration]);

  const handleConfigChange = useCallback(
    (
      key: string,
      config: ManualConfiguration | null,
      shouldReloadSchema: boolean,
    ) => {
      const nextConfig = triggerConfiguration.filter(
        (item) => item.schemaPath.join(".") !== key,
      );

      if (config) {
        nextConfig.push(config);
      }

      setTriggerConfiguration(nextConfig);
      if (shouldReloadSchema) {
        fetchSchema(nextConfig);
      }
    },
    [triggerConfiguration, fetchSchema],
  );

  const handleFormChange = useCallback(
    (data: JSONValue, shouldReloadSchema: boolean) => {
      if (!isJSONObject(data)) {
        return;
      }

      const nextConfig: ManualConfiguration[] = [];
      buildManualConfigFromFormData(data, [], nextConfig);
      setTriggerConfiguration(nextConfig);

      if (shouldReloadSchema) {
        fetchSchema(nextConfig);
      }
    },
    [fetchSchema],
  );

  const requiresConnection =
    triggerDefinition?.appConnectionConfig !== undefined;

  const formContext = useMemo<TriggerFormContext | undefined>(() => {
    if (!triggerDefinition) {
      return undefined;
    }

    return {
      configMap,
      triggerId: triggerDefinition.id,
      triggerConfiguration,
      connectionId: connectionId ?? undefined,
      onConfigChange: handleConfigChange,
      onRefreshSchema: fetchSchema,
    };
  }, [
    configMap,
    triggerDefinition,
    triggerConfiguration,
    connectionId,
    handleConfigChange,
    fetchSchema,
  ]);

  const handleSave = async () => {
    if (!triggerDefinition) {
      return;
    }

    await onSave({
      triggerId: triggerDefinition.id,
      triggerMetadata: {
        triggerConfiguration,
        connectionConfig: connectionId ? { connectionId } : undefined,
      },
      userMessage: userMessage.trim().length > 0 ? userMessage.trim() : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialTrigger ? "Edit Trigger" : "Configure Trigger"}
          </DialogTitle>
        </DialogHeader>

        {triggerDefinition ? (
          <div className="space-y-5">
            <div className="space-y-1">
              <p className="text-sm font-medium">{triggerDefinition.name}</p>
              <p className="text-sm text-muted-foreground">
                {triggerDefinition.description}
              </p>
            </div>

            {requiresConnection && triggerDefinition.appConnectionConfig && (
              <div className="space-y-2">
                <Label>App Connection</Label>
                <ConnectionSelector
                  appId={triggerDefinition.appConnectionConfig.appId}
                  providerId={triggerDefinition.appConnectionConfig.providerId}
                  connectionId={connectionId}
                  onSelect={setConnectionId}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="trigger-user-message">User Message</Label>
              <Textarea
                id="trigger-user-message"
                value={userMessage}
                onChange={(event) => setUserMessage(event.target.value)}
                placeholder="Optional user message to send when this trigger fires"
                rows={4}
              />
            </div>

            {schemaError && (
              <p className="text-sm text-destructive">{schemaError}</p>
            )}

            {isLoadingSchema && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading trigger schema</span>
              </div>
            )}

            {dynamicSchema &&
              (!requiresConnection || (requiresConnection && connectionId)) &&
              formContext && (
                <JSONSchemaForm<TriggerFormContext>
                  schema={dynamicSchema}
                  formData={formData}
                  onChange={handleFormChange}
                  formContext={formContext}
                  fieldTemplate={TriggerFieldTemplate}
                />
              )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={
                  isSaving ||
                  isLoadingSchema ||
                  !!schemaError ||
                  !triggerDefinition
                }
              >
                Save
                {isSaving && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No trigger selected.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

const isSchemaResponse = (data: unknown): data is { schema: JSONSchema7 } => {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  if (!("schema" in data)) {
    return false;
  }

  return typeof data.schema === "object" && data.schema !== null;
};
